import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Form, Col, Row, Button } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { IoIosAlert } from 'react-icons/io';

import api from '../../../../api';
import { toastrAdd } from '../../../../actions';

function UserEmailVerification({ users, email, setEmail, setEmailVerification }) {
	/**
     * statuses:
     * 0 === info
     * 1 === user exists
     * 2 === existing user link confirmation
     * 3 === user linked
     */
	const dispatch = useDispatch();
	const [status, setStatus] = useState(0);
	const { t } = useTranslation();

	const _verifyIfUserExists = ({ email }) => {
		api.user.checkIfUserExists(email)
			.then(response => {
				const { email, userID } = response.data.data.user;
                const exists = users.findIndex(user => user.userID === userID) > -1;

				if (exists) {
					setEmail(email);
					setStatus(1);
					dispatch(toastrAdd({
						type: 'warning',
						title: t('users.user_exists'),
						details: t('users.user_creation_user_exists', { email })
					}));
				} else {
					setStatus(2);
				}
			})
			.catch(error => {
				setEmail(email);
				setEmailVerification(false);
			});
	};

	const _linkUser = ({ email, sendEmail }) => {
		api.user.linkUserToPartner({
			email, 
			partnerID: localStorage.getItem('partnerID'),
			...(sendEmail ? { sendEmail: true } : {})
		})
		.then(() => {
			setEmail(email);
			setStatus(3);
			dispatch(toastrAdd({
				type: 'success',
				title: t('users.user_linked'),
				details: t('users.user_creation_user_linked', { email })
			}));
		})
		.catch(error => {
			dispatch(toastrAdd({
				type: 'error',
				title: 'Error',
				details: error.errors[0].details
			}));
		});
};

	const formik = useFormik({
		initialValues: { email: '', sendEmail: true },
		enableReinitialize: true,
		validationSchema: Yup.object().shape({
			email: Yup.string()
				.required(t('form.email_required'))
				.email(t('form.email_invalid'))
		}),
		onSubmit: _verifyIfUserExists
	});

	const { values, errors, touched } = formik;

	let variant;
	let message;

	if (status === 0) {
		variant = 'primary';
		message = t('users.user_creation_info');
	} else if (status === 1) {
		variant = 'warning';
		message = t('users.user_creation_user_added', { email });
	} else if (status === 2) {
		variant = "info";
		message = t('users.user_creation_user_exists', { email });
	} else if (status === 3) {
		variant = 'success';
		message = t('users.user_creation_user_linked', { email })
	}

	return (
		<>
			<Alert variant={variant} className="d-flex align-items-center">
				<div>
					<IoIosAlert size={30} style={{ marginRight: '10px' }} />
				</div>
				<div>
					{message}
				</div>
			</Alert>

			<Form onSubmit={formik.handleSubmit}>
				<Form.Group as={Row} controlId="email" className="required">
					<Form.Label column sm={3}>{t('general.email')}</Form.Label>
					<Col sm={9}>
						<Form.Control
							value={values.email || ''}
							onChange={formik.handleChange}
							disabled={status === 3}
							isInvalid={errors.email && touched.email}
							required
						/>
						{errors.email && touched.email ? (
							<Form.Control.Feedback type="invalid">
								{errors.email}
							</Form.Control.Feedback>
						) : null}
					</Col>
				</Form.Group>
				{status === 2 || status === 3 ?
					<Form.Group as={Row} controlId="sendEmail">
						<Col sm={{ span: 9, offset: 3 }}>
							<Form.Check
								type="checkbox"
								id="sendEmail"
								label={t('users.send_email_to_user')}
								value={values.sendEmail}
								defaultChecked
								onChange={formik.handleChange}
								disabled={status === 3}
							/>
							<span className="text-muted">
								{t('users.send_email_to_user_text')}
							</span>
						</Col>
					</Form.Group>
				: null}
				<Form.Group as={Row}>
					<Col sm={{ span: 9, offset: 3 }}>
						<Button
							type="button"
							variant="success"
							disabled={status === 3}
							onClick={() => {
								if (status === 0) {
									formik.handleSubmit();
								} else if (status === 2) {
									_linkUser(values);
								}
							}}
						>
							{(status === 0 || status === 1) ? t('users.save_user') : t('users.link_user')}
						</Button>
					</Col>
				</Form.Group>
			</Form>
		</>
	);
}

export default UserEmailVerification;